import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Minus, Info, PlusCircle, Check, AlertCircle, BedDouble, RotateCcw, AlertTriangle } from 'lucide-react';
import AddonSelectorDialog from './AddonSelectorDialog';
import { API_BASE_URL, getAuthHeaders } from '../../config/api.config';
import { useAuth } from '../../contexts/AuthContext';
import {
  calculateOptimalDistribution,
  calculateTotalBeds,
  getBedStatus,
  canProceedWithBooking,
  RoomType
} from '../../utils/accommodationAlgorithm';

const API_BASE = API_BASE_URL;

interface AddOnService {
  id: number;
  name: string;
  description: string;
  price: number;
  grossPrice?: number;
  taxRate?: number;
  pricingType: 'PerPerson' | 'PerGroup';
  categoryId: number;
  categoryName: string;
  isActive: boolean;
  isOptionalChoice?: boolean;
  isAccommodation?: boolean; // True wenn Übernachtungs-Add-On
  bedsPerUnit?: number; // Anzahl Betten pro Zimmer
}

// Types matching backend DTOs
interface CalculatedAddonDto {
  addOnServiceId: number;
  addOnServiceName: string;
  pricingType: string;
  quantityTotal: number;
  quantityFree: number;
  quantityPaid: number;
  pricePerUnit: number;
  grossPricePerUnit?: number; // Bruttopreis pro Einheit
  taxRate?: number; // Steuersatz (z.B. 0.19 für 19%)
  subtotal: number;
  isManuallyAdjusted: boolean;
  priceOverride?: number; // For manual price adjustment
  originalIncludedQuantity?: number; // Store the original free quantity from tour config
  originalPricingType?: string; // Store the original pricing type from tour config
  isOptionalChoice?: boolean; // If true, this is an optional choice addon (e.g., meal selection)
  categoryId?: number; // Category for grouping optional choice addons
  isFullyIncluded?: boolean; // True wenn komplett inkludiert (z.B. Getränkeflat)
  isIncludedInTourPrice?: boolean; // True wenn im Tourpreis enthalten
  quantityMultiplier?: number; // Multiplikator für optionale Inklusivleistungen (default: 1)
  isAccommodation?: boolean; // True wenn Übernachtungs-Add-On (Zimmer)
  bedsPerUnit?: number; // Anzahl Betten pro Zimmer
  isOptionalExtra?: boolean; // T-058 Stufe 2 (v1.8.61): optional zubuchbar (Menge 0 vorbelegt)
}

interface AddonAdjustment {
  addOnServiceId: number;
  addOnServiceName: string;
  pricingType: string;
  quantityTotal: number;
  pricePerUnit: number;
}

interface AddonSelectorProps {
  tourType: string; // STANDARD, PREMIUM, PRIVAT
  participantCount: number;
  onAddonsChange: (addons: CalculatedAddonDto[]) => void;
  initialAddons?: CalculatedAddonDto[];
}

const AddonSelector: React.FC<AddonSelectorProps> = ({
  tourType,
  participantCount,
  onAddonsChange,
  initialAddons = []
}) => {
  const { isAdmin } = useAuth();
  const isAdminUser = isAdmin();

  const [calculatedAddons, setCalculatedAddons] = useState<CalculatedAddonDto[]>(initialAddons);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddonDialog, setShowAddonDialog] = useState(false);

  // Format currency helper
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Track previous values to detect actual changes
  const prevTourTypeRef = React.useRef<string>(tourType);
  const prevParticipantCountRef = React.useRef<number>(participantCount);

  // Load auto-calculated addons when component mounts or tourType/participants change
  // BUT: Don't reload if we already have initialAddons (user navigated back)
  useEffect(() => {
    const tourTypeChanged = prevTourTypeRef.current !== tourType;
    const participantCountChanged = prevParticipantCountRef.current !== participantCount;

    // Only load from server if:
    // 1. No initialAddons provided (first time), OR
    // 2. tourType or participantCount actually changed (user modified settings in Step 1)
    if (initialAddons.length === 0 || tourTypeChanged || participantCountChanged) {
      loadCalculatedAddons();
    }

    // Update refs for next comparison
    prevTourTypeRef.current = tourType;
    prevParticipantCountRef.current = participantCount;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourType, participantCount]);

  const loadCalculatedAddons = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`[AddonSelector] Loading addons for ${tourType} with ${participantCount} participants`);

      const response = await fetch(
        `${API_BASE}/tour-types/${tourType}/addons/calculate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tourType,
            participantCount
          })
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to calculate addons: ${response.statusText}`);
      }

      const addons: CalculatedAddonDto[] = await response.json();
      console.log('[AddonSelector] Loaded calculated addons:', addons);

      // SCHRITT 1: Übernachtungs-Add-Ons mit Smart-Distribution-Algorithmus verarbeiten
      const accommodationAddons = addons.filter(a => a.isAccommodation && a.bedsPerUnit && a.bedsPerUnit > 0);
      const nonAccommodationAddons = addons.filter(a => !a.isAccommodation || !a.bedsPerUnit || a.bedsPerUnit <= 0);

      let processedAccommodationAddons: CalculatedAddonDto[] = [];
      if (accommodationAddons.length > 0) {
        console.log(`[AddonSelector] Found ${accommodationAddons.length} accommodation addons, applying smart distribution for ${participantCount} participants`);

        // Konvertiere zu RoomType für den Algorithmus
        const roomTypes: RoomType[] = accommodationAddons.map(addon => ({
          id: addon.addOnServiceId!,
          bedsPerUnit: addon.bedsPerUnit!,
          price: addon.pricePerUnit,
          grossPrice: addon.grossPricePerUnit || addon.pricePerUnit,
          name: addon.addOnServiceName
        }));

        // Berechne optimale Verteilung
        const optimalDistribution = calculateOptimalDistribution(participantCount, roomTypes);
        console.log('[AddonSelector] Optimal room distribution:', Object.fromEntries(optimalDistribution));

        // Wende Verteilung auf Addons an
        processedAccommodationAddons = accommodationAddons.map(addon => {
          const optimalQty = optimalDistribution.get(addon.addOnServiceId!) || 0;

          // Berechne free/paid basierend auf includedQuantity
          let quantityFree: number;
          let quantityPaid: number;

          if (addon.isIncludedInTourPrice) {
            // Komplett inklusive - alle Zimmer sind kostenlos
            quantityFree = optimalQty;
            quantityPaid = 0;
          } else {
            // NICHT im Tourpreis enthalten - ALLE Zimmer sind kostenpflichtig!
            // Das ist der korrekte initiale Zustand für nicht-inkludierte Unterkünfte
            quantityFree = 0;
            quantityPaid = optimalQty;
          }

          console.log(`[AddonSelector] Room ${addon.addOnServiceName}: optimal=${optimalQty}, free=${quantityFree}, paid=${quantityPaid}`);

          return {
            ...addon,
            quantityTotal: optimalQty,
            quantityFree,
            quantityPaid,
            subtotal: quantityPaid * addon.pricePerUnit,
            originalIncludedQuantity: (addon.pricingType === 'PerPerson')
              ? Math.min(addon.quantityFree || 0, participantCount)
              : addon.quantityFree || 0,
            originalPricingType: addon.pricingType
          };
        });
      }

      // SCHRITT 2: Nicht-Übernachtungs-Add-Ons verarbeiten (wie bisher)
      let isFirstOptionalChoice = true;
      const processedNonAccommodationAddons = nonAccommodationAddons.map(addon => {
        // Bestimme den effektiven Multiplikator
        // Gilt nur für optionale Inklusivleistungen (isIncludedInTourPrice = true UND isOptionalChoice = true)
        const multiplier = (addon.isIncludedInTourPrice && addon.isOptionalChoice && addon.quantityMultiplier)
          ? addon.quantityMultiplier
          : 1;

        console.log(`[AddonSelector] Addon ${addon.addOnServiceName}: pricingType=${addon.pricingType}, isOptionalChoice=${addon.isOptionalChoice}, isIncludedInTourPrice=${addon.isIncludedInTourPrice}, multiplier=${multiplier}`);

        let quantityTotal = addon.quantityTotal;
        let quantityFree = addon.quantityFree;
        let quantityPaid = addon.quantityPaid;

        // Berechne die maximale Menge mit Multiplikator
        const maxQuantityWithMultiplier = participantCount * multiplier;

        // Vorbelegung für Verpflegungsauswahl:
        // ERSTE Option: (Teilnehmerzahl × Multiplikator) vorbelegen
        // ALLE WEITEREN: 0 vorbelegen
        if (addon.isOptionalChoice) {
          if (isFirstOptionalChoice) {
            // Erste Option bekommt Teilnehmerzahl × Multiplikator als Vorbelegung
            quantityTotal = maxQuantityWithMultiplier;
            // Wenn im Tourpreis enthalten, ist alles kostenlos
            if (addon.isIncludedInTourPrice) {
              quantityFree = maxQuantityWithMultiplier;
              quantityPaid = 0;
            } else {
              quantityFree = Math.min(maxQuantityWithMultiplier, addon.quantityFree || 0);
              quantityPaid = Math.max(0, maxQuantityWithMultiplier - quantityFree);
            }
            isFirstOptionalChoice = false;
            console.log(`[AddonSelector] Vorbelegung ERSTE Option ${addon.addOnServiceName}: ${quantityTotal} (free=${quantityFree}, paid=${quantityPaid}, isIncludedInTourPrice=${addon.isIncludedInTourPrice})`);
          } else {
            // Alle weiteren Optionen starten bei 0
            quantityTotal = 0;
            quantityFree = 0;
            quantityPaid = 0;
            console.log(`[AddonSelector] Vorbelegung weitere Option ${addon.addOnServiceName}: 0`);
          }
        }

        return {
          ...addon,
          quantityTotal,
          quantityFree,
          quantityPaid,
          subtotal: quantityPaid * addon.pricePerUnit,
          originalIncludedQuantity: (addon.pricingType === 'PerPerson')
            ? Math.min(addon.quantityFree || 0, participantCount)
            : addon.quantityFree || 0,
          originalPricingType: addon.pricingType,
          quantityMultiplier: multiplier // Speichere den effektiven Multiplikator
        };
      });

      // Kombiniere alle Addons (Übernachtung zuerst, dann Rest)
      // Review v1.8.61 (T-058 Stufe 2): gewählte Mengen der optional zubuchbaren Speisen bleiben bei geänderter Teilnehmerzahl erhalten
      const vorher = calculatedAddons;
      const allAddons = [...processedAccommodationAddons, ...processedNonAccommodationAddons].map(addon => {
        if (!addon.isOptionalExtra) return addon;
        const alt = vorher.find(p => p.addOnServiceId === addon.addOnServiceId);
        if (!alt || !(alt.quantityTotal > 0)) return addon;
        const menge = alt.quantityTotal;
        return { ...addon, quantityTotal: menge, quantityFree: 0, quantityPaid: menge, subtotal: menge * addon.pricePerUnit, isManuallyAdjusted: true };
      });

      setCalculatedAddons(allAddons);
      onAddonsChange(allAddons);
    } catch (err: any) {
      console.error('[AddonSelector] Error loading addons:', err);
      setError(err.message || 'Fehler beim Laden der Zusatzoptionen');
      setCalculatedAddons([]);
      onAddonsChange([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to recalculate free/paid split
  const recalculateAddonQuantities = (addon: CalculatedAddonDto, newQuantity: number, effectivePrice: number) => {
    const originalIncludedQuantity = addon.originalIncludedQuantity ?? 0;

    // IMMER Begrenzung anwenden - KEIN Sonderfall für isIncludedInTourPrice!
    // Free units: maximal so viel wie ursprünglich inkludiert
    // Paid units: alles darüber hinaus
    const quantityFree = Math.min(newQuantity, originalIncludedQuantity);
    const quantityPaid = Math.max(0, newQuantity - originalIncludedQuantity);
    const subtotal = quantityPaid * effectivePrice;

    console.log('[AddonSelector] Recalculating:', {
      addonName: addon.addOnServiceName,
      newQuantity,
      originalIncludedQuantity,
      quantityFree,
      quantityPaid,
      subtotal
    });

    return { quantityFree, quantityPaid, subtotal };
  };

  const handleQuantityChange = (addon: CalculatedAddonDto, delta: number) => {
    const newQuantity = addon.quantityTotal + delta;
    if (newQuantity < 0) return;

    // Use override price if set, otherwise use original price
    const effectivePrice = addon.priceOverride ?? addon.pricePerUnit;

    // Recalculate free/paid split (initial calculation)
    const { quantityFree, quantityPaid, subtotal } = recalculateAddonQuantities(addon, newQuantity, effectivePrice);

    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      quantityTotal: newQuantity,
      quantityFree,
      quantityPaid,
      subtotal,
      isManuallyAdjusted: true
    };

    let updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    // If this is an accommodation addon, recalculate excess bed costs
    if (addon.isAccommodation && addon.isIncludedInTourPrice) {
      updatedAddons = recalculateAccommodationCosts(updatedAddons);
    }

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const handleQuantityInputChange = (addon: CalculatedAddonDto, value: string) => {
    const newQuantity = parseInt(value) || 0;
    if (newQuantity < 0) return;

    // Use override price if set, otherwise use original price
    const effectivePrice = addon.priceOverride ?? addon.pricePerUnit;

    // Recalculate free/paid split (initial calculation)
    const { quantityFree, quantityPaid, subtotal } = recalculateAddonQuantities(addon, newQuantity, effectivePrice);

    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      quantityTotal: newQuantity,
      quantityFree,
      quantityPaid,
      subtotal,
      isManuallyAdjusted: true
    };

    let updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    // If this is an accommodation addon, recalculate excess bed costs
    if (addon.isAccommodation && addon.isIncludedInTourPrice) {
      updatedAddons = recalculateAccommodationCosts(updatedAddons);
    }

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const handlePriceChange = (addon: CalculatedAddonDto, newPrice: number) => {
    if (newPrice < 0) return;

    // Recalculate subtotal with new price
    const subtotal = addon.quantityPaid * newPrice;

    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      priceOverride: newPrice,
      subtotal,
      isManuallyAdjusted: true
    };

    const updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const handlePricingTypeChange = (addon: CalculatedAddonDto, newType: string) => {
    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      pricingType: newType,
      isManuallyAdjusted: true
    };

    const updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const handleRemoveAddon = (addonId: number) => {
    const updatedAddons = calculatedAddons.filter(a => a.addOnServiceId !== addonId);
    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  const handleAddAddon = (addon: AddOnService) => {
    // Create a new calculated addon from the selected service
    // WICHTIG: taxRate und grossPrice müssen übernommen werden für korrekte MwSt-Berechnung!
    // WICHTIG: isAccommodation und bedsPerUnit müssen übernommen werden für Zimmer-Verteilung!
    const newAddon: CalculatedAddonDto = {
      addOnServiceId: addon.id,
      addOnServiceName: addon.name,
      pricingType: addon.pricingType,
      quantityTotal: 0, // Start with 0, user can increase
      quantityFree: 0,
      quantityPaid: 0,
      pricePerUnit: addon.price,
      grossPricePerUnit: addon.grossPrice, // Bruttopreis übernehmen
      taxRate: addon.taxRate, // Steuersatz übernehmen (7% oder 19%)
      subtotal: 0,
      isManuallyAdjusted: true, // Mark as manually added
      priceOverride: undefined,
      originalIncludedQuantity: 0, // Manually added addons have no included quantity
      originalPricingType: addon.pricingType, // Store original pricing type
      // Accommodation-Eigenschaften aus dem AddOnService übernehmen
      isAccommodation: addon.isAccommodation || false,
      bedsPerUnit: addon.bedsPerUnit || undefined,
      // Manuell hinzugefügte Addons sind nicht im Tourpreis enthalten
      isIncludedInTourPrice: false
    };

    console.log(`[AddonSelector] Adding manual addon: ${addon.name}, isAccommodation=${addon.isAccommodation}, bedsPerUnit=${addon.bedsPerUnit}`);

    let updatedAddons = [...calculatedAddons, newAddon];

    // Wenn es ein Übernachtungs-Addon ist, Kosten neu berechnen
    if (newAddon.isAccommodation && newAddon.bedsPerUnit) {
      updatedAddons = recalculateAccommodationCosts(updatedAddons);
    }

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  // Netto-Gesamtpreis (für interne Berechnungen)
  const calculateTotalPrice = (): number => {
    return calculatedAddons.reduce((sum, addon) => sum + addon.subtotal, 0);
  };

  // Brutto-Einzelpreis berechnen (mit individuellem Steuersatz!)
  const getAddonGrossPrice = (addon: CalculatedAddonDto): number => {
    const taxRate = addon.taxRate ?? 0.19; // Individueller Steuersatz (7% oder 19%)
    const netPrice = addon.priceOverride ?? addon.pricePerUnit;

    // Wenn priceOverride gesetzt, müssen wir neu berechnen
    // Sonst verwenden wir grossPricePerUnit vom Backend (falls vorhanden)
    if (addon.priceOverride !== undefined && addon.priceOverride !== null) {
      return netPrice * (1 + taxRate);
    }
    return addon.grossPricePerUnit ?? netPrice * (1 + taxRate);
  };

  // Brutto-Gesamtpreis (für Anzeige - Brutto hat Vorrang!)
  const calculateTotalPriceGross = (): number => {
    return calculatedAddons.reduce((sum, addon) => {
      const grossPrice = getAddonGrossPrice(addon);
      const grossSubtotal = addon.quantityPaid * grossPrice;
      return sum + grossSubtotal;
    }, 0);
  };

  // NEU: Steueraufschlüsselung berechnen (Basis = BRUTTO)
  // Für jedes Addon: Netto = Brutto / (1 + taxRate), MwSt = Brutto - Netto
  const calculateTaxBreakdown = (): {
    totalGross: number;
    totalNet: number;
    vat7: number;
    vat19: number
  } => {
    let totalGross = 0;
    let totalNet = 0;
    let vat7 = 0;
    let vat19 = 0;

    calculatedAddons.forEach(addon => {
      if (addon.quantityPaid === 0) return; // Keine Kosten = keine MwSt.

      const grossPrice = getAddonGrossPrice(addon);
      const grossSubtotal = addon.quantityPaid * grossPrice;
      const taxRate = addon.taxRate ?? 0.19;

      // Netto aus Brutto berechnen
      const netSubtotal = grossSubtotal / (1 + taxRate);
      const vatAmount = grossSubtotal - netSubtotal;

      totalGross += grossSubtotal;
      totalNet += netSubtotal;

      // MwSt. nach Steuersatz aufschlüsseln
      if (Math.abs(taxRate - 0.07) < 0.001) {
        vat7 += vatAmount;
      } else {
        vat19 += vatAmount;
      }
    });

    return { totalGross, totalNet, vat7, vat19 };
  };

  // Brutto-Subtotal für einzelnes Addon berechnen
  const getAddonSubtotalGross = (addon: CalculatedAddonDto): number => {
    const grossPrice = getAddonGrossPrice(addon);
    return addon.quantityPaid * grossPrice;
  };

  // AUFGABE 1: Korrekte Anzeige von "inklusive" vs "kostenpflichtig"
  const getTotalQuantityInfo = (): string => {
    const totalFree = calculatedAddons.reduce((sum, a) => sum + a.quantityFree, 0);
    const totalPaid = calculatedAddons.reduce((sum, a) => sum + a.quantityPaid, 0);

    // Wenn alles inklusive ist (quantityPaid === 0), zeige "inklusive" statt "kostenlos"
    if (totalFree > 0 && totalPaid > 0) {
      return `${totalFree} inklusive, ${totalPaid} kostenpflichtig`;
    } else if (totalFree > 0 && totalPaid === 0) {
      return `${totalFree} inklusive`;
    } else if (totalPaid > 0) {
      return `${totalPaid} kostenpflichtig`;
    }
    return 'Keine Addons';
  };

  // AUFGABE 3: Komplett inkludierte Add-Ons für Gast ausblenden
  // Ein Add-On ist "komplett inkludiert" wenn:
  // - quantityFree >= participantCount UND
  // - keine manuelle Erhöhung über die inkludierte Menge hinaus
  const shouldShowAddonToGuest = (addon: CalculatedAddonDto): boolean => {
    // Admin sieht immer alles
    if (isAdminUser) return true;

    // Wenn das Add-On komplett inklusive ist und keine Zusatzkosten anfallen,
    // braucht der Gast es nicht zu sehen (z.B. Getränkeflat)
    const isFullyIncluded = addon.isFullyIncluded ||
      (addon.originalIncludedQuantity !== undefined &&
       addon.originalIncludedQuantity >= participantCount &&
       addon.quantityPaid === 0);

    // Gast soll komplett inkludierte Add-Ons nicht sehen
    return !isFullyIncluded;
  };

  // Gefilterte Add-Ons basierend auf Benutzerrolle
  const visibleAddons = calculatedAddons.filter(shouldShowAddonToGuest);

  // NEUER BEREICH: Übernachtungs-Add-Ons (isAccommodation === true)
  const accommodationAddons = visibleAddons.filter(a => a.isAccommodation && a.bedsPerUnit && a.bedsPerUnit > 0);

  // Bereich 1: Auswahl pro Teilnehmer (isOptionalChoice === true, NICHT Übernachtung)
  const optionalChoiceAddons = visibleAddons.filter(a => a.isOptionalChoice && !a.isAccommodation);

  // Bereich 2 & 3: Nicht-optionale Addons aufteilen (NICHT Übernachtung)
  const regularAddons = visibleAddons.filter(a => !a.isOptionalChoice && !a.isAccommodation);

  // Bereich 2: Inklusivleistungen (quantityPaid === 0 UND quantityTotal > 0 = ausgewählt und komplett inklusiv)
  const inclusiveAddons = regularAddons.filter(a => a.quantityPaid === 0 && a.quantityTotal > 0);

  // Bereich 3: Extras (quantityPaid > 0 ODER quantityTotal === 0 = kostenpflichtig oder noch nicht ausgewählt)
  const extraAddons = regularAddons.filter(a => a.quantityPaid > 0 || a.quantityTotal === 0);

  // ===== ÜBERNACHTUNGS-FUNKTIONEN =====
  // Berechne Gesamt-Betten aus Übernachtungs-Addons
  const getAccommodationTotalBeds = useMemo(() => {
    return accommodationAddons.reduce((sum, addon) => {
      return sum + (addon.quantityTotal * (addon.bedsPerUnit || 1));
    }, 0);
  }, [accommodationAddons]);

  // Berechne überschüssige Betten (Betten über Teilnehmerzahl)
  const getExcessBeds = useMemo(() => {
    return Math.max(0, getAccommodationTotalBeds - participantCount);
  }, [getAccommodationTotalBeds, participantCount]);

  /**
   * Berechnet kostenpflichtige Zimmer basierend auf Tour-Konfiguration
   *
   * WICHTIG: Unterscheidung nach isIncludedInTourPrice:
   * - isIncludedInTourPrice = true: Zimmer sind im Tourpreis enthalten, nur Überschuss wird berechnet
   * - isIncludedInTourPrice = false: ALLE Zimmer werden zusätzlich berechnet (kostenpflichtig)
   */
  const recalculateAccommodationCosts = useCallback((addons: CalculatedAddonDto[]): CalculatedAddonDto[] => {
    // Filter for ALL accommodation addons
    const accommodationList = addons.filter(a => a.isAccommodation && a.bedsPerUnit);
    if (accommodationList.length === 0) return addons;

    // Berechne totale Betten (nur für inkludierte Zimmer relevant)
    const includedAccommodations = accommodationList.filter(a => a.isIncludedInTourPrice);
    const totalIncludedBeds = includedAccommodations.reduce((sum, a) => sum + (a.quantityTotal * (a.bedsPerUnit || 1)), 0);

    console.log(`[recalculateAccommodationCosts] participants=${participantCount}, includedBeds=${totalIncludedBeds}`);

    // Berechne Excess-Betten nur für inkludierte Zimmer
    const excessBeds = Math.max(0, totalIncludedBeds - participantCount);
    console.log(`[Accommodation] Excess beds (from included): ${excessBeds}`);

    // Sortiere inkludierte Zimmer nach Betten (kleinste zuerst für Excess-Berechnung)
    const sortedIncluded = [...includedAccommodations].sort((a, b) =>
      (a.bedsPerUnit || 1) - (b.bedsPerUnit || 1)
    );

    let remainingExcess = excessBeds;
    const excessAllocation = new Map<number, number>(); // addonId -> überschüssige Zimmer

    // Verteile überschüssige Betten auf inkludierte Zimmer (kleinste zuerst)
    for (const addon of sortedIncluded) {
      if (remainingExcess <= 0) break;

      const bedsPerRoom = addon.bedsPerUnit || 1;
      const roomsInThisType = addon.quantityTotal;

      const excessRoomsForType = Math.min(
        Math.ceil(remainingExcess / bedsPerRoom),
        roomsInThisType
      );

      if (excessRoomsForType > 0) {
        excessAllocation.set(addon.addOnServiceId!, excessRoomsForType);
        remainingExcess -= excessRoomsForType * bedsPerRoom;
        console.log(`[Accommodation] ${addon.addOnServiceName}: ${excessRoomsForType} Zimmer excess`);
      }
    }

    // Wende Berechnung auf alle Addons an
    return addons.map(addon => {
      if (!addon.isAccommodation) return addon;

      // WICHTIG: Unterscheidung nach isIncludedInTourPrice!
      if (addon.isIncludedInTourPrice) {
        // Im Tourpreis enthalten: Nur Überschuss-Zimmer sind kostenpflichtig
        const excessRooms = excessAllocation.get(addon.addOnServiceId!) || 0;
        const freeRooms = addon.quantityTotal - excessRooms;

        console.log(`[Accommodation] ${addon.addOnServiceName} (INCLUDED): total=${addon.quantityTotal}, free=${freeRooms}, paid=${excessRooms}`);

        return {
          ...addon,
          quantityFree: freeRooms,
          quantityPaid: excessRooms,
          subtotal: excessRooms * addon.pricePerUnit
        };
      } else {
        // NICHT im Tourpreis enthalten: ALLE Zimmer sind kostenpflichtig!
        console.log(`[Accommodation] ${addon.addOnServiceName} (NOT INCLUDED): total=${addon.quantityTotal}, ALL PAID`);

        return {
          ...addon,
          quantityFree: 0,
          quantityPaid: addon.quantityTotal,
          subtotal: addon.quantityTotal * addon.pricePerUnit
        };
      }
    });
  }, [participantCount]);

  // Betten-Status (ok/error/warning)
  const accommodationBedStatus = useMemo(() => {
    if (accommodationAddons.length === 0) return 'ok';
    return getBedStatus(getAccommodationTotalBeds, participantCount);
  }, [accommodationAddons.length, getAccommodationTotalBeds, participantCount]);

  // Kann Buchung fortgesetzt werden? (Admin kann immer, Kunde nur mit genug Betten)
  const canProceedWithAccommodation = useMemo(() => {
    if (accommodationAddons.length === 0) return true;
    return canProceedWithBooking(getAccommodationTotalBeds, participantCount, isAdminUser);
  }, [accommodationAddons.length, getAccommodationTotalBeds, participantCount, isAdminUser]);

  // Reset auf optimale Verteilung
  const handleResetAccommodationToOptimal = () => {
    if (accommodationAddons.length === 0) return;

    // RoomTypes für Algorithmus
    const roomTypes: RoomType[] = accommodationAddons.map(addon => ({
      id: addon.addOnServiceId!,
      bedsPerUnit: addon.bedsPerUnit!,
      price: addon.pricePerUnit,
      grossPrice: addon.grossPricePerUnit || addon.pricePerUnit,
      name: addon.addOnServiceName
    }));

    const optimalDistribution = calculateOptimalDistribution(participantCount, roomTypes);

    // Update alle Übernachtungs-Addons mit optimaler Verteilung
    let updatedAddons = calculatedAddons.map(addon => {
      if (!addon.isAccommodation || !addon.bedsPerUnit) return addon;

      const optimalQty = optimalDistribution.get(addon.addOnServiceId!) || 0;

      return {
        ...addon,
        quantityTotal: optimalQty,
        quantityFree: optimalQty, // Will be recalculated below
        quantityPaid: 0,
        subtotal: 0,
        isManuallyAdjusted: false
      };
    });

    // Recalculate costs for excess beds (if any)
    updatedAddons = recalculateAccommodationCosts(updatedAddons);

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  // Betten-Status Farben
  const getBedStatusColors = (status: string) => {
    switch (status) {
      case 'ok':
        return { bg: '#dcfce7', border: '#16a34a', text: '#166534' };
      case 'error':
        return { bg: '#fef2f2', border: '#dc2626', text: '#991b1b' };
      case 'warning':
        return { bg: '#fef9c3', border: '#ca8a04', text: '#854d0e' };
      default:
        return { bg: '#f3f4f6', border: '#9ca3af', text: '#4b5563' };
    }
  };

  // Berechne das Gesamtmaximum für optionale Addons (Teilnehmer × Multiplikator)
  // Der Multiplikator ist für alle optionalen Addons eines Typs gleich
  const getOptionalChoiceMaxTotal = (): number => {
    if (optionalChoiceAddons.length === 0) return participantCount;
    // Nimm den Multiplikator des ersten Addons (sollten alle gleich sein)
    const multiplier = optionalChoiceAddons[0]?.quantityMultiplier || 1;
    return participantCount * multiplier;
  };

  // Calculate total for optional choice addons
  const getOptionalChoiceTotal = (): number => {
    return optionalChoiceAddons.reduce((sum, addon) => sum + addon.quantityTotal, 0);
  };

  // Check if optional choice validation passes
  const isOptionalChoiceValid = (): boolean => {
    if (optionalChoiceAddons.length === 0) return true;
    const maxTotal = getOptionalChoiceMaxTotal();
    return getOptionalChoiceTotal() === maxTotal;
  };

  // Get max quantity for a specific optional choice addon
  const getMaxForOptionalAddon = (addon: CalculatedAddonDto): number => {
    const currentTotal = getOptionalChoiceTotal();
    const maxTotal = getOptionalChoiceMaxTotal();
    const remaining = maxTotal - currentTotal;
    return addon.quantityTotal + remaining;
  };

  // Handle quantity change for optional choice addons with limit
  const handleOptionalChoiceQuantityChange = (addon: CalculatedAddonDto, delta: number) => {
    const newQuantity = addon.quantityTotal + delta;
    if (newQuantity < 0) return;

    // Check if we would exceed the max total (participantCount × multiplier)
    const currentTotal = getOptionalChoiceTotal();
    const maxTotal = getOptionalChoiceMaxTotal();
    const newTotal = currentTotal - addon.quantityTotal + newQuantity;
    if (newTotal > maxTotal) return;

    // Use override price if set, otherwise use original price
    const effectivePrice = addon.priceOverride ?? addon.pricePerUnit;

    // Recalculate free/paid split
    const { quantityFree, quantityPaid, subtotal } = recalculateAddonQuantities(addon, newQuantity, effectivePrice);

    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      quantityTotal: newQuantity,
      quantityFree,
      quantityPaid,
      subtotal,
      isManuallyAdjusted: true
    };

    const updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  // Handle input change for optional choice addons with limit
  const handleOptionalChoiceInputChange = (addon: CalculatedAddonDto, value: string) => {
    const newQuantity = parseInt(value) || 0;
    if (newQuantity < 0) return;

    // Calculate max allowed for this addon
    const maxAllowed = getMaxForOptionalAddon(addon);
    const clampedQuantity = Math.min(Math.max(0, newQuantity), maxAllowed);

    // Use override price if set, otherwise use original price
    const effectivePrice = addon.priceOverride ?? addon.pricePerUnit;

    // Recalculate free/paid split
    const { quantityFree, quantityPaid, subtotal } = recalculateAddonQuantities(addon, clampedQuantity, effectivePrice);

    const updatedAddon: CalculatedAddonDto = {
      ...addon,
      quantityTotal: clampedQuantity,
      quantityFree,
      quantityPaid,
      subtotal,
      isManuallyAdjusted: true
    };

    const updatedAddons = calculatedAddons.map(a =>
      a.addOnServiceId === addon.addOnServiceId ? updatedAddon : a
    );

    setCalculatedAddons(updatedAddons);
    onAddonsChange(updatedAddons);
  };

  // AUFGABE 1 + 4: Helper für Preis-Anzeige (Brutto vorrangig, Admin sieht auch Netto)
  const renderPriceDisplay = (addon: CalculatedAddonDto) => {
    const taxRate = addon.taxRate ?? 0.19; // Individueller Steuersatz!
    const netPrice = addon.priceOverride ?? addon.pricePerUnit;
    const grossPrice = getAddonGrossPrice(addon); // Verwendet individuelle Steuersätze
    const taxPercent = (taxRate * 100).toFixed(0);

    if (isAdminUser) {
      // Admin sieht Brutto (vorrangig) + Netto + MwSt
      return (
        <div style={{ fontSize: '0.85rem' }}>
          <div style={{ fontWeight: 'bold', color: '#16a34a', fontSize: '1rem' }}>
            {formatCurrency(grossPrice)}
          </div>
          <div style={{ color: '#666', fontSize: '0.8rem' }}>
            Netto: {formatCurrency(netPrice)} ({taxPercent}% MwSt.)
          </div>
        </div>
      );
    } else {
      // Gast sieht nur Bruttopreis (wenn kostenpflichtig)
      return (
        <div style={{ fontSize: '0.85rem', color: '#666' }}>
          {formatCurrency(grossPrice)} pro {addon.pricingType === 'PerPerson' ? 'Person' : 'Gruppe'}
        </div>
      );
    }
  };

  // AUFGABE 1: Korrekte Anzeige des Status (inklusive vs kostenpflichtig)
  const renderAddonStatus = (addon: CalculatedAddonDto) => {
    // Wenn alles inklusive (quantityPaid === 0)
    if (addon.quantityPaid === 0 && addon.quantityFree > 0) {
      return (
        <div style={{
          background: '#dcfce7',
          border: '1px solid #16a34a',
          padding: '0.5rem 0.75rem',
          borderRadius: '6px',
          marginBottom: '0.75rem',
          fontSize: '0.85rem',
          color: '#15803d',
          textAlign: 'center',
          fontWeight: 'bold'
        }}>
          inklusive
        </div>
      );
    }

    // Wenn teilweise inklusive
    if (addon.quantityFree > 0 && addon.quantityPaid > 0) {
      return (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          padding: '0.5rem 0.75rem',
          borderRadius: '6px',
          marginBottom: '0.75rem',
          fontSize: '0.85rem',
          color: '#92400e'
        }}>
          <div>{addon.quantityFree} inklusive</div>
          <div>{addon.quantityPaid} kostenpflichtig</div>
        </div>
      );
    }

    // Wenn alles kostenpflichtig
    if (addon.quantityPaid > 0) {
      return (
        <div style={{
          background: '#fee2e2',
          border: '1px solid #dc2626',
          padding: '0.5rem 0.75rem',
          borderRadius: '6px',
          marginBottom: '0.75rem',
          fontSize: '0.85rem',
          color: '#dc2626',
          textAlign: 'center'
        }}>
          {addon.quantityPaid} kostenpflichtig
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div className="spinner" style={{ margin: '0 auto', width: '48px', height: '48px' }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '1rem',
        background: '#fee',
        border: '1px solid #fcc',
        borderRadius: '8px',
        color: '#c00'
      }}>
        <strong>Fehler:</strong> {error}
        <button
          onClick={loadCalculatedAddons}
          style={{
            marginLeft: '1rem',
            padding: '0.5rem 1rem',
            background: '#4a9eff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div className="addon-selector">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <div>
          <h3 style={{ margin: '0 0 0.5rem 0' }}>
            Leistungen für {tourType}
          </h3>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            {participantCount} Teilnehmer · {getTotalQuantityInfo()}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setShowAddonDialog(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: '#4a9eff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            <PlusCircle size={18} />
            Weitere Zusatzoption
          </button>
        </div>
      </div>

      {/* Bereich 1: Auswahl pro Teilnehmer (isOptionalChoice === true) */}
      {optionalChoiceAddons.length > 0 && (
        <div style={{
          background: '#f0fdf4',
          border: '2px solid #16a34a',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '1.5rem' }}>🎯</span>
            <h3 style={{ margin: 0, color: '#15803d' }}>Auswahl pro Teilnehmer</h3>
          </div>
          <p style={{ color: '#166534', marginBottom: '1rem', fontSize: '0.9rem' }}>
            {(() => {
              const maxTotal = getOptionalChoiceMaxTotal();
              const multiplier = optionalChoiceAddons[0]?.quantityMultiplier || 1;
              if (multiplier > 1) {
                return `Bitte verteilen Sie ${maxTotal} Portionen (${participantCount} Teilnehmer × ${multiplier} pro Person).`;
              }
              return `Bitte wählen Sie für jeden Teilnehmer eine Option. Die Summe muss ${participantCount} ergeben.`;
            })()}
          </p>

          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}>
            {optionalChoiceAddons.map(addon => {
              const maxForThisAddon = getMaxForOptionalAddon(addon);
              const optionalTotal = getOptionalChoiceTotal();

              return (
                <div
                  key={addon.addOnServiceId}
                  style={{
                    background: 'white',
                    border: '1px solid #86efac',
                    borderRadius: '8px',
                    padding: '1rem'
                  }}
                >
                  <h4 style={{ margin: '0 0 0.5rem 0', color: '#1a2332' }}>
                    {addon.addOnServiceName}
                  </h4>

                  {/* AUFGABE 4: Admin sieht Netto+Brutto, Gast nur "inklusive" oder Aufpreis */}
                  {isAdminUser ? (
                    renderPriceDisplay(addon)
                  ) : (
                    // Gast: Zeige "inklusive" wenn alles free, sonst Aufpreis
                    addon.quantityPaid === 0 && addon.quantityTotal > 0 ? (
                      <p style={{ fontSize: '0.85rem', color: '#16a34a', marginBottom: '0.75rem', fontWeight: 'bold' }}>
                        inklusive
                      </p>
                    ) : addon.quantityPaid > 0 ? (
                      <p style={{ fontSize: '0.85rem', color: '#dc2626', marginBottom: '0.75rem' }}>
                        {/* FIX: Use addon.taxRate for correct VAT (7% for food, 19% for services) */}
                        Aufpreis: {formatCurrency((addon.grossPricePerUnit ?? addon.pricePerUnit * (1 + (addon.taxRate ?? 0.19))) * addon.quantityPaid)}
                      </p>
                    ) : null
                  )}

                  {/* Quantity Controls */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleOptionalChoiceQuantityChange(addon, -1)}
                      disabled={addon.quantityTotal === 0}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        background: addon.quantityTotal === 0 ? '#f0f0f0' : '#dcfce7',
                        cursor: addon.quantityTotal === 0 ? 'not-allowed' : 'pointer',
                        opacity: addon.quantityTotal === 0 ? 0.5 : 1
                      }}
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type="number"
                      value={addon.quantityTotal}
                      onChange={(e) => handleOptionalChoiceInputChange(addon, e.target.value)}
                      min="0"
                      max={maxForThisAddon}
                      style={{
                        width: '5rem',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '1.2rem',
                        padding: '0.5rem',
                        border: '2px solid #16a34a',
                        borderRadius: '6px'
                      }}
                    />
                    <button
                      onClick={() => handleOptionalChoiceQuantityChange(addon, 1)}
                      disabled={optionalTotal >= getOptionalChoiceMaxTotal()}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #16a34a',
                        borderRadius: '4px',
                        background: optionalTotal >= getOptionalChoiceMaxTotal() ? '#f0f0f0' : '#16a34a',
                        color: optionalTotal >= getOptionalChoiceMaxTotal() ? '#999' : 'white',
                        cursor: optionalTotal >= getOptionalChoiceMaxTotal() ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>

                  {/* AUFGABE 1: Status-Anzeige (inklusive vs kostenpflichtig) */}
                  {addon.quantityTotal > 0 && renderAddonStatus(addon)}
                </div>
              );
            })}
          </div>

          {/* Validation Status */}
          <div style={{
            marginTop: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: isOptionalChoiceValid() ? '#dcfce7' : '#fef9c3',
            border: isOptionalChoiceValid() ? '1px solid #16a34a' : '1px solid #facc15'
          }}>
            {(() => {
              const maxTotal = getOptionalChoiceMaxTotal();
              const currentTotal = getOptionalChoiceTotal();
              const multiplier = optionalChoiceAddons[0]?.quantityMultiplier || 1;

              if (isOptionalChoiceValid()) {
                return (
                  <>
                    <Check size={20} style={{ color: '#16a34a' }} />
                    <span style={{ color: '#15803d', fontWeight: 'bold' }}>
                      {multiplier > 1
                        ? `Alle ${maxTotal} Portionen verteilt (${currentTotal}/${maxTotal})`
                        : `Alle ${participantCount} Teilnehmer haben eine Auswahl (${currentTotal}/${maxTotal})`
                      }
                    </span>
                  </>
                );
              } else {
                const remaining = maxTotal - currentTotal;
                return (
                  <>
                    <AlertCircle size={20} style={{ color: '#ca8a04' }} />
                    <span style={{ color: '#854d0e', fontWeight: 'bold' }}>
                      {multiplier > 1
                        ? `Noch ${remaining} Portion(en) offen (${currentTotal}/${maxTotal})`
                        : `Noch ${remaining} Auswahl(en) offen (${currentTotal}/${maxTotal})`
                      }
                    </span>
                  </>
                );
              }
            })()}
          </div>
        </div>
      )}

      {/* NEUER BEREICH: Übernachtung / Zimmerbelegung */}
      {accommodationAddons.length > 0 && (
        <div style={{
          background: getBedStatusColors(accommodationBedStatus).bg,
          border: `2px solid ${getBedStatusColors(accommodationBedStatus).border}`,
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          {/* Header mit Betten-Counter */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <BedDouble size={28} style={{ color: getBedStatusColors(accommodationBedStatus).border }} />
              <div>
                <h3 style={{ margin: 0, color: getBedStatusColors(accommodationBedStatus).text }}>
                  Übernachtung
                </h3>
                <div style={{
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  color: getBedStatusColors(accommodationBedStatus).text
                }}>
                  Betten: {getAccommodationTotalBeds} / {participantCount} benötigt
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {accommodationBedStatus === 'ok' && <Check size={24} style={{ color: '#16a34a' }} />}
              {accommodationBedStatus === 'error' && <AlertTriangle size={24} style={{ color: '#dc2626' }} />}
              {accommodationBedStatus === 'warning' && <Info size={24} style={{ color: '#ca8a04' }} />}
              <button
                onClick={handleResetAccommodationToOptimal}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  padding: '0.5rem 0.75rem',
                  background: 'white',
                  border: '1px solid #94a3b8',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  color: '#64748b',
                  cursor: 'pointer'
                }}
              >
                <RotateCcw size={14} />
                Vorschlag
              </button>
            </div>
          </div>

          {/* Status-Meldung */}
          <div style={{
            padding: '0.5rem 0.75rem',
            marginBottom: '1rem',
            borderRadius: '6px',
            fontSize: '0.9rem',
            background: 'rgba(255,255,255,0.5)',
            color: getBedStatusColors(accommodationBedStatus).text
          }}>
            {accommodationBedStatus === 'ok' && `Ausreichend Schlafplätze für alle ${participantCount} Teilnehmer`}
            {accommodationBedStatus === 'error' && (
              <>
                <strong>Es fehlen {participantCount - getAccommodationTotalBeds} Betten!</strong>
                {!isAdminUser && ' Bitte wählen Sie mehr Zimmer.'}
                {isAdminUser && ' (Admin: Fortfahren möglich)'}
              </>
            )}
            {accommodationBedStatus === 'warning' && `${getAccommodationTotalBeds - participantCount} Betten mehr als benötigt (kostet mehr)`}
          </div>

          {/* Zimmer-Karten */}
          <div style={{
            display: 'grid',
            gap: '0.75rem',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))'
          }}>
            {accommodationAddons
              .sort((a, b) => (b.bedsPerUnit || 1) - (a.bedsPerUnit || 1))
              .map(addon => (
              <div
                key={addon.addOnServiceId}
                style={{
                  background: 'white',
                  border: addon.quantityTotal > 0 ? '1px solid #86efac' : '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '1rem'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.75rem'
                }}>
                  <div>
                    <h4 style={{ margin: '0 0 0.25rem 0', color: '#1e293b' }}>
                      {addon.addOnServiceName}
                    </h4>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                      {addon.bedsPerUnit} {addon.bedsPerUnit === 1 ? 'Bett' : 'Betten'} pro Zimmer
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{
                      fontSize: '0.85rem',
                      color: addon.quantityTotal > 0 ? '#16a34a' : '#94a3b8'
                    }}>
                      {addon.quantityTotal * (addon.bedsPerUnit || 1)} Betten
                    </div>
                  </div>
                </div>

                {/* Status-Badge */}
                {renderAddonStatus(addon)}

                {/* Quantity Controls */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleQuantityChange(addon, -1)}
                      disabled={addon.quantityTotal === 0}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        background: addon.quantityTotal === 0 ? '#f0f0f0' : 'white',
                        cursor: addon.quantityTotal === 0 ? 'not-allowed' : 'pointer',
                        opacity: addon.quantityTotal === 0 ? 0.5 : 1
                      }}
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type="number"
                      value={addon.quantityTotal}
                      onChange={(e) => handleQuantityInputChange(addon, e.target.value)}
                      min="0"
                      style={{
                        width: '4rem',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <button
                      onClick={() => handleQuantityChange(addon, 1)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #16a34a',
                        borderRadius: '4px',
                        background: '#16a34a',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>

                  {/* Gesamtpreis für dieses Zimmer */}
                  <div style={{
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    color: addon.quantityPaid > 0 ? '#16a34a' : '#94a3b8'
                  }}>
                    {formatCurrency(addon.subtotal)}
                  </div>
                </div>

                {/* Admin: Preis anpassen */}
                {isAdminUser && addon.quantityTotal > 0 && (
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.5rem',
                    background: '#f8f9fa',
                    borderRadius: '4px',
                    fontSize: '0.85rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <label>Preis/Einheit:</label>
                      <input
                        type="number"
                        value={addon.priceOverride ?? addon.pricePerUnit}
                        onChange={(e) => handlePriceChange(addon, parseFloat(e.target.value) || 0)}
                        step="0.01"
                        min="0"
                        style={{
                          width: '80px',
                          padding: '0.25rem 0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      />
                      <span>€</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Keine Addons vorhanden */}
      {visibleAddons.length === 0 && optionalChoiceAddons.length === 0 && accommodationAddons.length === 0 && (
        <div style={{
          padding: '2rem',
          textAlign: 'center',
          background: '#f8f9fa',
          borderRadius: '8px',
          color: '#666'
        }}>
          <Info size={48} style={{ margin: '0 auto 1rem' }} />
          <p>Keine automatischen Leistungen für diesen Tour-Typ konfiguriert.</p>
        </div>
      )}

      {/* Bereich 2: Inklusivleistungen (quantityPaid === 0) */}
      {inclusiveAddons.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <span style={{ fontSize: '1.5rem' }}>✅</span>
            <h3 style={{ margin: 0, color: '#16a34a' }}>Inklusivleistungen</h3>
          </div>
          <div style={{
            display: 'grid',
            gap: '1rem',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))'
          }}>
            {inclusiveAddons.map(addon => (
              <div
                key={addon.addOnServiceId}
                style={{
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  padding: '1rem',
                  background: addon.subtotal > 0 ? '#f0f8ff' : 'white',
                  transition: 'all 0.2s'
                }}
              >
                {/* Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#1a2332' }}>
                      {addon.addOnServiceName}
                    </h4>
                    {/* AUFGABE 4: Admin sieht Netto+Brutto+MwSt */}
                    {renderPriceDisplay(addon)}

                    {/* Pricing Type Selector - nur für Admin */}
                    {isAdminUser && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <label style={{ fontSize: '0.85rem', color: '#666', minWidth: '60px' }}>
                          Gültigkeit:
                        </label>
                        <select
                          value={addon.pricingType}
                          onChange={(e) => handlePricingTypeChange(addon, e.target.value)}
                          style={{
                            padding: '0.25rem 0.5rem',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            background: 'white',
                            cursor: 'pointer'
                          }}
                        >
                          <option value="PerPerson">Pro Person</option>
                          <option value="PerGroup">Pro Gruppe</option>
                          <option value="Fixed">Festpreis</option>
                          <option value="PerNight">Pro Nacht</option>
                          <option value="PerHour">Pro Stunde</option>
                          <option value="PerDay">Pro Tag</option>
                        </select>
                      </div>
                    )}
                  </div>
                  {addon.isManuallyAdjusted && isAdminUser && (
                    <span style={{
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      background: '#fef3c7',
                      color: '#92400e',
                      borderRadius: '4px'
                    }}>
                      Angepasst
                    </span>
                  )}
                </div>

                {/* AUFGABE 1: Status-Badge (inklusive/kostenpflichtig) */}
                {renderAddonStatus(addon)}

                {/* Quantity Breakdown - Detailed view (nur für Admin) */}
                {isAdminUser && (
                  <div style={{
                    background: '#f8f9fa',
                    padding: '0.75rem',
                    borderRadius: '6px',
                    marginBottom: '0.75rem',
                    fontSize: '0.9rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span>Gesamt:</span>
                      <strong>{addon.quantityTotal}</strong>
                    </div>
                    {addon.quantityFree > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem', color: '#16a34a' }}>
                        <span>Inklusiv:</span>
                        <span>-{addon.quantityFree}</span>
                      </div>
                    )}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      paddingTop: '0.5rem',
                      borderTop: '1px solid #ddd',
                      color: addon.quantityPaid > 0 ? '#dc2626' : '#666'
                    }}>
                      <span>Kostenpflichtig:</span>
                      <strong>{addon.quantityPaid}</strong>
                    </div>
                  </div>
                )}

                {/* Quantity Controls */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleQuantityChange(addon, -1)}
                      disabled={addon.quantityTotal === 0}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: addon.quantityTotal === 0 ? 'not-allowed' : 'pointer',
                        opacity: addon.quantityTotal === 0 ? 0.5 : 1
                      }}
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type="number"
                      value={addon.quantityTotal}
                      onChange={(e) => handleQuantityInputChange(addon, e.target.value)}
                      min="0"
                      style={{
                        width: '4rem',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <button
                      onClick={() => handleQuantityChange(addon, 1)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #4a9eff',
                        borderRadius: '4px',
                        background: '#4a9eff',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>

                  {addon.quantityTotal > 0 && isAdminUser && (
                    <button
                      onClick={() => handleRemoveAddon(addon.addOnServiceId)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: '#fee',
                        border: '1px solid #fcc',
                        borderRadius: '4px',
                        color: '#c00',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Entfernen
                    </button>
                  )}
                </div>

                {/* Price Configuration - nur für Admin */}
                {isAdminUser && (
                  <div style={{
                    padding: '0.75rem',
                    background: addon.subtotal > 0 ? '#f0fdf4' : '#f8f9fa',
                    borderRadius: '6px'
                  }}>
                    {/* Price per unit - editable */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem'
                    }}>
                      <label style={{ fontSize: '0.85rem', color: '#666', minWidth: '80px' }}>
                        Preis/Einheit:
                      </label>
                      <input
                        type="number"
                        value={addon.priceOverride ?? addon.pricePerUnit}
                        onChange={(e) => handlePriceChange(addon, parseFloat(e.target.value) || 0)}
                        step="0.01"
                        min="0"
                        style={{
                          padding: '0.25rem 0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.9rem',
                          width: '100px',
                          background: addon.priceOverride ? '#fef3c7' : 'white'
                        }}
                      />
                      <span style={{ fontSize: '0.85rem', color: '#666' }}>€</span>
                      {addon.priceOverride && (
                        <span style={{ fontSize: '0.75rem', color: '#92400e' }}>
                          (Original: {formatCurrency(addon.pricePerUnit)})
                        </span>
                      )}
                    </div>

                    {/* Total calculation */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      paddingTop: '0.5rem',
                      borderTop: '1px solid #ddd'
                    }}>
                      <div style={{ fontSize: '0.9rem', color: '#666' }}>
                        {addon.quantityPaid > 0 ? (
                          <>
                            {addon.quantityPaid}× (Brutto)
                            {addon.quantityFree > 0 && (
                              <div style={{ fontSize: '0.8rem', color: '#16a34a', marginTop: '0.25rem' }}>
                                ({addon.quantityFree}× inklusive)
                              </div>
                            )}
                          </>
                        ) : (
                          <span style={{ color: '#16a34a', fontWeight: 'bold' }}>Komplett inklusive</span>
                        )}
                      </div>
                      <strong style={{
                        fontSize: '1.1rem',
                        color: '#16a34a'
                      }}>
                        {formatCurrency(getAddonSubtotalGross(addon))}
                      </strong>
                    </div>
                  </div>
                )}

                {/* Gast: Zeige nur Gesamtpreis (Brutto) wenn kostenpflichtig */}
                {!isAdminUser && addon.quantityPaid > 0 && (
                  <div style={{
                    padding: '0.75rem',
                    background: '#f0fdf4',
                    borderRadius: '6px',
                    textAlign: 'right'
                  }}>
                    <strong style={{ fontSize: '1.1rem', color: '#16a34a' }}>
                      {formatCurrency(getAddonSubtotalGross(addon))}
                    </strong>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bereich 3: Extras (quantityPaid > 0) */}
      {extraAddons.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <span style={{ fontSize: '1.5rem' }}>➕</span>
            <h3 style={{ margin: 0, color: '#dc2626' }}>Zusätzliche Speisen</h3>
          </div>
          <div style={{
            display: 'grid',
            gap: '1rem',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))'
          }}>
            {extraAddons.map(addon => (
              <div
                key={addon.addOnServiceId}
                style={{
                  border: '2px solid #fecaca',
                  borderRadius: '8px',
                  padding: '1rem',
                  background: '#fef2f2',
                  transition: 'all 0.2s'
                }}
              >
                {/* Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#1a2332' }}>
                      {addon.addOnServiceName}
                    </h4>
                    {renderPriceDisplay(addon)}
                  </div>
                  {addon.isManuallyAdjusted && isAdminUser && (
                    <span style={{
                      fontSize: '0.75rem',
                      padding: '0.25rem 0.5rem',
                      background: '#fef3c7',
                      color: '#92400e',
                      borderRadius: '4px'
                    }}>
                      Angepasst
                    </span>
                  )}
                </div>

                {/* Status-Badge */}
                {renderAddonStatus(addon)}

                {/* Quantity Controls */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.75rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleQuantityChange(addon, -1)}
                      disabled={addon.quantityTotal === 0}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        background: 'white',
                        cursor: addon.quantityTotal === 0 ? 'not-allowed' : 'pointer',
                        opacity: addon.quantityTotal === 0 ? 0.5 : 1
                      }}
                    >
                      <Minus size={16} />
                    </button>
                    <input
                      type="number"
                      value={addon.quantityTotal}
                      onChange={(e) => handleQuantityInputChange(addon, e.target.value)}
                      min="0"
                      style={{
                        width: '4rem',
                        textAlign: 'center',
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        padding: '0.5rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px'
                      }}
                    />
                    <button
                      onClick={() => handleQuantityChange(addon, 1)}
                      style={{
                        padding: '0.5rem',
                        border: '1px solid #dc2626',
                        borderRadius: '4px',
                        background: '#dc2626',
                        color: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>

                  {addon.quantityTotal > 0 && isAdminUser && (
                    <button
                      onClick={() => handleRemoveAddon(addon.addOnServiceId)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: '#fee',
                        border: '1px solid #fcc',
                        borderRadius: '4px',
                        color: '#c00',
                        cursor: 'pointer',
                        fontSize: '0.85rem'
                      }}
                    >
                      Entfernen
                    </button>
                  )}
                </div>

                {/* Gesamtpreis (Brutto) */}
                <div style={{
                  padding: '0.75rem',
                  background: 'white',
                  borderRadius: '6px',
                  textAlign: 'right'
                }}>
                  <strong style={{ fontSize: '1.1rem', color: '#dc2626' }}>
                    {formatCurrency(getAddonSubtotalGross(addon))}
                  </strong>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Total Summary - Bruttopreise mit Vorrang */}
      {visibleAddons.filter(a => a.quantityTotal > 0).length > 0 && (
        <div style={{
          marginTop: '1.5rem',
          padding: '1.25rem',
          background: '#f0fdf4',
          border: '2px solid #16a34a',
          borderRadius: '8px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h3 style={{ margin: '0 0 0.25rem 0', color: '#1a2332' }}>
                Extras Gesamt
              </h3>
              <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
                {visibleAddons.filter(a => a.quantityTotal > 0).length} Addon(s) ausgewählt
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              {(() => {
                const taxBreakdown = calculateTaxBreakdown();
                return (
                  <>
                    {/* Bruttopreis prominent */}
                    <div style={{
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                      color: '#16a34a'
                    }}>
                      {formatCurrency(taxBreakdown.totalGross)}
                    </div>
                    {/* Nettopreis und MwSt.-Aufschlüsselung für Admin */}
                    {isAdminUser && (
                      <>
                        <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                          Netto: {formatCurrency(taxBreakdown.totalNet)}
                        </div>
                        {taxBreakdown.vat7 > 0 && (
                          <div style={{ fontSize: '0.85rem', color: '#666' }}>
                            MwSt. 7% (anteilig): {formatCurrency(taxBreakdown.vat7)}
                          </div>
                        )}
                        {taxBreakdown.vat19 > 0 && (
                          <div style={{ fontSize: '0.85rem', color: '#666' }}>
                            MwSt. 19% (anteilig): {formatCurrency(taxBreakdown.vat19)}
                          </div>
                        )}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Addon Selector Dialog */}
      <AddonSelectorDialog
        isOpen={showAddonDialog}
        onClose={() => setShowAddonDialog(false)}
        onSelect={handleAddAddon}
        excludeIds={calculatedAddons.map(a => a.addOnServiceId)}
        tourType={tourType}
      />

      <style>{`
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #4a9eff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AddonSelector;
